import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { actorText } from '../utils/orderActor';
import { formatPhoneIntl } from '../utils/phone';
import { selectedOptions } from '../utils/serviceLines';
import { sizeMismatchText } from '../utils/sizeMismatch';
import {
  ApiError,
  getOrderDetail,
  getOrderInvoices,
  getOrderInvoiceDownloadUrl,
  markServiceComplete,
  markVehicleReceived,
  markCollectedByCustomer,
  InvoiceDocument,
  OrderDetail,
  OrderListItem,
  SettlementStatusLine,
} from '../services/api';
import {
  canMarkCollectedByCustomer,
  canMarkVehicleReceived,
  hasGaragatLeg,
  legScheduledText,
  methodLabel,
  pickupLegLabel,
  resolveLegMethods,
  returnLegLabel,
  transportLabel,
} from '../utils/orderLegs';

function statusColor(status: string): { bg: string; fg: string } {
  switch (status) {
    case 'CREATED':
    case 'PENDING':
      return { bg: '#fef3c7', fg: '#854d0e' };
    case 'PICKUP_ASSIGNED':
    case 'PICKUP_SCHEDULED':
    case 'CAR_PICKED_UP':
    case 'CAR_DELIVERED_TO_GARAGE':
    case 'DROPOFF_ASSIGNED':
    case 'ACTIVE':
      return { bg: '#dbeafe', fg: '#1e40af' };
    case 'IN_SERVICE':
    case 'IN_PROGRESS':
    case 'SERVICE_COMPLETE':
      return { bg: '#ede9fe', fg: '#5b21b6' };
    case 'READY':
    case 'VEHICLE_ACCEPTED':
      return { bg: '#d1fae5', fg: '#065f46' };
    case 'COMPLETED':
    case 'CAR_RETURNED':
    case 'SETTLED':
    case 'CLOSED':
    case 'ADMIN_CLOSED':
      return { bg: '#d1fae5', fg: '#065f46' };
    case 'CANCELLED':
    case 'REFUNDED':
    case 'VEHICLE_REJECTED':
    case 'DISPUTED':
      return { bg: '#fee2e2', fg: '#991b1b' };
    default:
      return { bg: '#e5e7eb', fg: '#374151' };
  }
}

function titleCase(raw: string): string {
  return raw
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function legLabel(type: string): string {
  switch (type) {
    case 'PICKUP':
      return 'Pickup driver';
    case 'RETURN_DELIVERY':
    case 'DELIVERY':
      return 'Return driver';
    default:
      return titleCase(type);
  }
}


const EVENT_LABELS: Record<string, string> = {
  ORDER_CREATED: 'Order created',
  STATUS_CHANGED: 'Status changed',
  DRIVER_ASSIGNED: 'Driver assigned',
  DRIVER_CONFIRMED: 'Driver confirmed',
  DRIVER_REJECTED: 'Driver declined',
  DRIVER_REASSIGNED: 'Driver reassigned',
  VEHICLE_ACCEPTED: 'Vehicle accepted',
  IN_PROGRESS: 'Service in progress',
  SERVICE_COMPLETE: 'Service completed',
  COMPLETED: 'Completed',
  DISPUTED: 'Disputed',
  CANCELLED: 'Cancelled',
};

function eventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? titleCase(eventType);
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ minWidth: 160, color: '#6b7280', fontSize: '0.875rem', fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#1e293b', fontSize: '0.875rem', flex: 1 }}>{value}</span>
    </div>
  );
}

interface CardProps {
  title: string;
  children: React.ReactNode;
}

function Card({ title, children }: CardProps) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      border: '1px solid #e2e8f0',
      padding: '20px 24px',
      marginBottom: 16,
    }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>{title}</h3>
      {children}
    </div>
  );
}

/** The order actions the garage can perform from this page. */
type ActionKey = 'service-complete' | 'vehicle-received' | 'collected';

// One button in the Actions panel. `done` keeps the button on screen but inert after
// the transition landed, so a second press is impossible from the UI even though the
// endpoint itself is idempotent (Spec 053 AC-36).
function ActionButton({ label, busyLabel, doneLabel, busy, done, disabled, background, onClick }: {
  label: string;
  busyLabel: string;
  doneLabel: string;
  busy: boolean;
  done: boolean;
  disabled: boolean;
  background: string;
  onClick: () => void;
}) {
  const inert = done || disabled || busy;
  return (
    <button
      type="button"
      disabled={inert}
      onClick={onClick}
      style={{
        padding: '10px 24px',
        background: done ? '#e5e7eb' : background,
        color: done ? '#374151' : 'white',
        border: 'none',
        borderRadius: 8,
        fontWeight: 700,
        fontSize: '0.9375rem',
        cursor: inert ? 'not-allowed' : 'pointer',
        opacity: inert && !done ? 0.6 : 1,
        fontFamily: 'inherit',
      }}
    >
      {done ? doneLabel : busy ? busyLabel : label}
    </button>
  );
}

// A single line in the earnings breakdown; deductions render negative in the warning colour,
// the total is emphasised. Amount is formatted to 2 decimals with the currency.
function PayoutRow({ label, value, currency, muted, deduct, total, testId }:
  { label: string; value: number; currency: string; muted?: boolean; deduct?: boolean; total?: boolean; testId?: string }) {
  const color = total ? '#1e293b' : deduct ? '#b45309' : muted ? '#6b7280' : '#1e293b';
  const amount = `${value < 0 ? '- ' : ''}${Math.abs(value).toFixed(2)} ${currency}`;
  return (
    <div data-testid={testId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '7px 0' }}>
      <span style={{ color: total ? '#1e293b' : '#6b7280', fontSize: total ? '0.9375rem' : '0.875rem', fontWeight: total ? 700 : 500 }}>{label}</span>
      <span style={{ color, fontSize: total ? '1.0625rem' : '0.9375rem', fontWeight: total ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>{amount}</span>
    </div>
  );
}

/** Spec 068 §0b — a Garagat charge renders iff it is > 0; null, absent and 0.00 all mean "no row". */
function positive(value: number | null | undefined): number | null {
  return value != null && value > 0 ? Math.round(value * 100) / 100 : null;
}

// Spec 053 AC-21b — the server label already embeds the number for "In settlement STL-…";
// split it off so the number renders exactly once (as a link when the settlement id is known).
// "Paid on <date>" carries the number as a separate field, so it is appended after the text.
function splitSettlementLabel(line: SettlementStatusLine): { text: string; number?: string } {
  const number = line.settlementNumber ?? undefined;
  if (!number) return { text: line.label };
  const idx = line.label.indexOf(number);
  if (idx === -1) return { text: line.label, number };
  return { text: (line.label.slice(0, idx) + line.label.slice(idx + number.length)).trim(), number };
}

function settlementStateLabel(s: string): string {
  switch (s) {
    case 'AWAITING_HOLD': return 'Awaiting release';
    case 'ELIGIBLE': return 'Ready for next payout';
    case 'PENDING_SETTLEMENT': return 'In a settlement';
    case 'SETTLED': return 'Paid';
    case 'ON_HOLD_DISPUTE': return 'On hold (dispute)';
    default: return s;
  }
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const auth = useAuth();

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Only one action can be in flight at a time; the key of the firing button drives
  // its busy label while every other button stays disabled.
  const [pendingAction, setPendingAction] = useState<ActionKey | null>(null);
  const [completedActions, setCompletedActions] = useState<Partial<Record<ActionKey, boolean>>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const actionLoading = pendingAction !== null;

  // Spec 051 AC-9 — the garage's copy of the customer invoice / credit notes.
  // Failures never break the page: null or empty → the block simply isn't rendered.
  const [invoices, setInvoices] = useState<InvoiceDocument[]>([]);
  const [invoiceNotice, setInvoiceNotice] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const isOwner = auth.user?.roles.includes('GARAGE_OWNER') ?? false;
  const isManager = auth.user?.roles.includes('GARAGE_MANAGER') ?? false;
  const canManage = isOwner || isManager;

  const fetchOrder = useCallback(async () => {
    if (!auth.token || !orderId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getOrderDetail(orderId, auth.token);
      setOrder(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [auth.token, orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    if (!auth.token || !orderId) return;
    getOrderInvoices(orderId, auth.token)
      .then((res) => setInvoices(res.documents ?? []))
      .catch(() => setInvoices([])); // non-fatal — the rest of the page must not break
  }, [auth.token, orderId]);

  const handleDownloadInvoice = async (doc: InvoiceDocument) => {
    if (!auth.token || !orderId) return;
    setInvoiceNotice(null);
    setDownloadingDocId(doc.id);
    try {
      const { downloadUrl } = await getOrderInvoiceDownloadUrl(orderId, doc.id, auth.token);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 409 || err.code === 'INVOICE_NOT_READY')) {
        setInvoiceNotice('This document is still being prepared — please try again in a few minutes.');
      } else {
        setInvoiceNotice('Could not download the document. Please try again.');
      }
    } finally {
      setDownloadingDocId(null);
    }
  };

  const runAction = async (
    key: ActionKey,
    call: (orderId: string, token: string) => Promise<OrderListItem>,
    confirmText?: string,
  ) => {
    if (!auth.token || !orderId) return;
    if (confirmText && !window.confirm(confirmText)) return;
    setPendingAction(key);
    setActionError(null);
    try {
      // The action endpoints answer with the LIST-shaped OrderResponse, so the response is
      // deliberately discarded: feeding it into `order` would drop the payout breakdown, the
      // timeline, the pickup address and `assignedStaffUserIds` (which `isAssignedStaff` — and
      // therefore the whole Actions panel — is derived from). Re-read the detail instead.
      await call(orderId, auth.token);
      setCompletedActions((prev) => ({ ...prev, [key]: true }));
      // A refetch failure must not be reported as an action failure — the action already landed.
      try {
        setOrder(await getOrderDetail(orderId, auth.token));
      } catch {
        setActionError('Done, but the page could not be refreshed. Reload to see the new status.');
      }
    } catch (err) {
      // Spec 053 BR-23 — a 409 carries the server's explanation ("Transition not
      // allowed for this order's delivery choice"); show it verbatim rather than a
      // generic failure, because it tells the garage *why* the action is refused.
      setActionError(
        err instanceof ApiError
          ? err.message || `Action failed (${err.status})`
          : err instanceof Error
            ? err.message
            : 'Action failed',
      );
    } finally {
      setPendingAction(null);
    }
  };

  const handleMarkServiceComplete = () => runAction('service-complete', markServiceComplete);

  const handleVehicleReceived = () =>
    runAction(
      'vehicle-received',
      markVehicleReceived,
      'Confirm the vehicle has been received at the garage? The customer is notified and the service starts.',
    );

  const handleCollectedByCustomer = () =>
    runAction(
      'collected',
      markCollectedByCustomer,
      'Confirm the customer has collected the vehicle? This completes the order.',
    );

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: '#6b7280' }}>Loading order…</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: '#991b1b', marginBottom: 12 }}>{error || 'Order not found'}</div>
        <Link to="/orders" style={{ color: '#4f46e5', textDecoration: 'none', fontSize: '0.875rem' }}>
          ← Back to Orders
        </Link>
      </div>
    );
  }

  const sc = statusColor(order.status);
  const displayId = order.orderNumber ?? `#${order.id.slice(0, 8).toUpperCase()}`;
  const vehiclePlate = order.vehiclePlate ?? order.vehicleSummary?.plate ?? '';
  const vehicleMakeModel =
    order.vehicleMakeModel ??
    (order.vehicleSummary
      ? [order.vehicleSummary.make, order.vehicleSummary.model].filter(Boolean).join(' ')
      : '');
  const hasVehicle = Boolean(vehiclePlate || vehicleMakeModel);
  const options = selectedOptions(order);
  // #1435 — the booked size is smaller than the saved vehicle (warn, never block).
  const sizeNote = sizeMismatchText(order);
  // Spec 053 Phase 3 — per-leg methods, falling back to the legacy whole-order mirror.
  const legs = resolveLegMethods(order);
  const garagatPickup = legs.pickup === 'GARAGAT';

  // Action panel logic:
  // "Mark finished" — IN_PROGRESS → SERVICE_COMPLETE, for owner/manager or assigned staff
  const isAssignedStaff = auth.user?.id
    ? (order.assignedStaffUserIds?.includes(auth.user.id) ?? false)
    : false;
  const canAct = canManage || isAssignedStaff;
  const showMarkComplete = order.status === 'IN_PROGRESS' && canAct;

  // BR-22 / AC-36 — offered only where the transition is actually reachable for this
  // order's legs. Once fired, the button stays on screen but inert (`completedActions`)
  // so the garage sees the action landed instead of it silently vanishing.
  const showVehicleReceived =
    canAct && (canMarkVehicleReceived(order) || Boolean(completedActions['vehicle-received']));
  const showCollected =
    canAct && (canMarkCollectedByCustomer(order) || Boolean(completedActions['collected']));
  const showActions = showMarkComplete || showVehicleReceived || showCollected;

  return (
    <div style={{ padding: 24, maxWidth: 860, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: 20, fontSize: '0.875rem' }}>
        <Link to="/orders" style={{ color: '#4f46e5', textDecoration: 'none' }}>Orders</Link>
        <span style={{ margin: '0 8px', color: '#9ca3af' }}>›</span>
        <span style={{ color: '#6b7280' }}>{displayId}</span>
      </div>

      {/* Top section: order number, status, customer */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
            {displayId}
          </h1>
          <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 6,
            background: sc.bg, color: sc.fg, fontSize: '0.8125rem', fontWeight: 700,
          }}>
            {order.status}
          </span>
          {order.breakdown?.promoApplied && (
            <span data-testid="promo-badge" style={{
              display: 'inline-block', padding: '4px 12px', borderRadius: 6,
              background: '#fffbd1', color: '#030c54', fontSize: '0.8125rem', fontWeight: 600,
            }}>
              Garagat promotion
            </span>
          )}
          {sizeNote && (
            <span data-testid="size-mismatch-badge" style={{
              display: 'inline-block', padding: '4px 12px', borderRadius: 6,
              background: '#fef3c7', color: '#92400e', fontSize: '0.8125rem', fontWeight: 600,
            }}>
              {sizeNote}
              {order.sizeWarningAcknowledged && (
                <span data-testid="size-warning-acknowledged" style={{ fontWeight: 400 }}>
                  {' '}· customer saw the warning
                </span>
              )}
            </span>
          )}
          {hasGaragatLeg(order) && (
            <span style={{
              display: 'inline-block', padding: '4px 12px', borderRadius: 6,
              background: '#ede9fe', color: '#5b21b6', fontSize: '0.8125rem', fontWeight: 600,
            }}>
              {methodLabel(order)}
            </span>
          )}
        </div>

      </div>

      {/* Customer card — name + phone, prominent so the garage can reach the customer */}
      <Card title="Customer">
        <InfoRow label="Name" value={order.customerName || '—'} />
        {order.customerPhone ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Phone</span>
            <a
              href={`tel:${formatPhoneIntl(order.customerPhone)}`}
              style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: 600, fontSize: '0.9375rem' }}
            >
              {formatPhoneIntl(order.customerPhone)}
            </a>
          </div>
        ) : (
          <InfoRow label="Phone" value="—" />
        )}
      </Card>

      {/* Vehicle card — the submitted car */}
      {hasVehicle && (
        <Card title="Vehicle">
          {vehicleMakeModel && <InfoRow label="Car" value={vehicleMakeModel} />}
          {vehiclePlate && <InfoRow label="Plate" value={vehiclePlate} />}
        </Card>
      )}

      {/* Service card */}
      <Card title="Service Details">
        {order.serviceCategory && <InfoRow label="Category" value={order.serviceCategory} />}
        {order.serviceSubcategory && <InfoRow label="Sub-category" value={order.serviceSubcategory} />}
        {order.serviceName && <InfoRow label="Service" value={order.serviceName} />}
        {/* #1193 — any selection, a single variant's picked values included; a flat service has none. */}
        {options.length > 0 && (
          <InfoRow
            label="Options"
            value={
              <ul data-testid="service-lines" style={{ margin: 0, paddingLeft: 18 }}>
                {options.map((l, i) => (
                  <li key={i}>
                    {l.label} — {Number(l.price).toFixed(2)} {order.currency}
                  </li>
                ))}
              </ul>
            }
          />
        )}
        {order.scheduledDate && (
          <InfoRow
            label="Scheduled Date"
            value={
              <>
                {order.scheduledDate}
                {(order.pickupTimeSlotLabel ?? order.pickupSlotLabel) && (
                  <span style={{ color: '#6b7280' }}>
                    {' '}· {order.pickupTimeSlotLabel ?? order.pickupSlotLabel}
                  </span>
                )}
              </>
            }
          />
        )}
        {(order as unknown as { durationDays?: number }).durationDays != null && (
          <InfoRow
            label="Duration"
            value={`${(order as unknown as { durationDays: number }).durationDays} day${(order as unknown as { durationDays: number }).durationDays !== 1 ? 's' : ''}`}
          />
        )}
        {/* Shown standalone only when there's no scheduled date to attach the slot to. */}
        {!order.scheduledDate && (order.pickupTimeSlotLabel ?? order.pickupSlotLabel) && (
          <InfoRow label="Pickup Slot" value={order.pickupTimeSlotLabel ?? order.pickupSlotLabel} />
        )}
        <InfoRow
          label="Total"
          value={<strong>{order.totalAmount.toFixed(2)} {order.currency}</strong>}
        />
        {/* Spec 053 AC-21b — settlement status line. The settlement number links to the
            partner's settlement detail when the id is present; otherwise it stays plain text. */}
        {order.settlementStatusLine?.label && (() => {
          const { text, number } = splitSettlementLabel(order.settlementStatusLine);
          const settlementId = order.settlementStatusLine.settlementId;
          return (
            <InfoRow
              label="Settlement"
              value={
                <span data-testid="settlement-status-line">
                  {text && <span>{text}</span>}
                  {text && number && ' '}
                  {number && (settlementId ? (
                    <Link
                      to={`/earnings/${settlementId}`}
                      style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8125rem' }}
                    >
                      {number}
                    </Link>
                  ) : (
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: '#374151' }}>{number}</span>
                  ))}
                </span>
              }
            />
          );
        })()}
      </Card>

      {/* Pickup & delivery card (Spec 053 Phase 3) — each leg is bought separately, so
          the garage needs to know per leg whether a Garagat driver is coming or the
          customer handles it. The self-leg wording is the contract copy. */}
      <Card title="Pickup & delivery">
        <InfoRow label="Pickup" value={<span data-testid="pickup-leg">{pickupLegLabel(order)}</span>} />
        <InfoRow label="Return" value={<span data-testid="return-leg">{returnLegLabel(order)}</span>} />
        {hasGaragatLeg(order) && (
          <p style={{ margin: '10px 0 0', fontSize: '0.75rem', color: '#9ca3af', lineHeight: 1.5 }}>
            Pickup &amp; delivery is provided by Garagat
            {order.deliveryTrips ? ` (${order.deliveryTrips} trip${order.deliveryTrips === 1 ? '' : 's'})` : ''}
            {' '}— it is charged to the customer by Garagat and does not affect your payout.
          </p>
        )}
      </Card>

      {/* Customer note card (#414) — the note (and optional photo) the customer attached
          when booking. The image URL is a presigned download URL, never a storage key. */}
      {(order.notes || order.noteImageUrl) && (
        <Card title="Customer note">
          {order.notes && (
            <p style={{ margin: 0, color: '#1e293b', fontSize: '0.875rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {order.notes}
            </p>
          )}
          {order.noteImageUrl && (
            <a
              href={order.noteImageUrl}
              target="_blank"
              rel="noreferrer"
              title="Open full size"
              style={{ display: 'inline-block', marginTop: order.notes ? 12 : 0 }}
            >
              <img
                src={order.noteImageUrl}
                alt="Customer note attachment"
                style={{
                  width: 120,
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  display: 'block',
                }}
              />
            </a>
          )}
        </Card>
      )}

      {/* Spec 068 V17 / AC-22 — two blocks. "Customer paid" is INFORMATION: what the customer was
          charged, Garagat's own supplies (pickup & delivery, service fee) listed beside the service.
          "Your earnings" is the garage's money: the service price less commission — no Garagat fee
          ever appears there. Rows for Garagat's supplies render only when > 0. */}
      {order.breakdown && (() => {
        const b = order.breakdown;
        const deliveryGross = positive(order.deliveryFeeGross) ?? positive((b.platformFee ?? 0) + (b.platformFeeVat ?? 0));
        const serviceFeeGross = positive(b.serviceFeeGross);
        const servicePrice = b.serviceGross ?? order.totalAmount;
        return (
          <>
            <Card title="Customer paid">
              <div data-testid="customer-paid-block">
                <PayoutRow label="Service price" value={servicePrice} currency={order.currency} />
                {deliveryGross != null && (
                  <PayoutRow label="Pickup & delivery (Garagat)" value={deliveryGross} currency={order.currency} muted testId="customer-paid-delivery" />
                )}
                {serviceFeeGross != null && (
                  <PayoutRow label="Service fee (Garagat)" value={serviceFeeGross} currency={order.currency} muted testId="customer-paid-service-fee" />
                )}
                <div className="order-breakdown-total">
                  <PayoutRow
                    label={b.promoApplied ? 'Order value' : 'Customer paid'}
                    value={b.totalPayable} currency={order.currency} total testId="customer-paid-total"
                  />
                </div>
                {/* Spec 059 AC-63 — with a Garagat promotion the customer paid less, but every figure here is
                    the full order value: Garagat funds the discount and the payout is unchanged. */}
                {b.promoApplied && (
                  <p data-testid="promo-earnings-note" className="order-breakdown-promo-note">
                    Garagat promotion applied — funded by Garagat, your earnings are not affected.
                  </p>
                )}
                {(deliveryGross != null || serviceFeeGross != null) && (
                  <p className="order-breakdown-note">
                    Items marked (Garagat) are Garagat&apos;s own charges to the customer. They are not part of your earnings.
                  </p>
                )}
              </div>
            </Card>

            {b.partnerNet != null && (
              <Card title="Your earnings">
                <div data-testid="your-earnings-block">
                  <PayoutRow label="Service price" value={servicePrice} currency={order.currency} />
                  <PayoutRow
                    label={`Garagat commission${b.commissionRate != null ? ` (${Math.round(b.commissionRate * 100)}%)` : ''}`}
                    value={-(b.commissionAmount ?? 0)} currency={order.currency} deduct
                  />
                  <PayoutRow label="VAT on commission (5%)" value={-(b.vatOnCommission ?? 0)} currency={order.currency} deduct />
                  <div className="order-breakdown-total">
                    <PayoutRow label="Net to you" value={b.partnerNet} currency={order.currency} total testId="your-earnings-net" />
                  </div>
                  {/* Spec 049 fallback — hidden once the Spec 053 status line (Service Details card) is present. */}
                  {!order.settlementStatusLine?.label && order.settlementState && order.settlementState !== 'NOT_APPLICABLE' && (
                    <div style={{ marginTop: 10, fontSize: '0.8125rem', color: '#6b7280' }}>
                      Settlement: <strong style={{ color: '#1e293b' }}>{settlementStateLabel(order.settlementState)}</strong>
                    </div>
                  )}
                  {b.vatApplicable && (
                    <p className="order-breakdown-note">
                      The service price includes VAT ({Math.round(b.vatRate * 100)}%: {b.vatAmount.toFixed(2)} {order.currency}), collected on your behalf and remitted to the FTA — it is included in your payout.
                    </p>
                  )}
                </div>
              </Card>
            )}
          </>
        );
      })()}

      {/* Invoices card (Spec 051 AC-9) — the garage's legal copy of the customer
          invoice + any credit notes. Rendered only when documents exist. */}
      {invoices.length > 0 && (
        <Card title="Invoices">
          {invoices.map((doc) => {
            const isIssued = doc.status === 'ISSUED';
            const label = doc.docType === 'CREDIT_NOTE' ? 'Credit note' : 'Invoice';
            return (
              <div
                key={doc.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  padding: '10px 0', borderBottom: '1px solid #f3f4f6',
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                    {label}{' '}
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {isIssued && doc.invoiceNumber ? doc.invoiceNumber : ''}
                    </span>
                    {!isIssued && (
                      <span style={{
                        display: 'inline-block', marginInlineStart: 6, padding: '2px 10px',
                        borderRadius: 6, background: '#fef3c7', color: '#854d0e',
                        fontSize: '0.75rem', fontWeight: 700,
                      }}>
                        Being prepared
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                    {doc.issueDate
                      ? `Issued ${new Date(doc.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
                      : 'Not issued yet'}
                    {doc.docType === 'CREDIT_NOTE' && doc.originalInvoiceNumber
                      ? ` · for ${doc.originalInvoiceNumber}`
                      : ''}
                  </div>
                </div>
                {isIssued && (
                  <button
                    onClick={() => handleDownloadInvoice(doc)}
                    disabled={downloadingDocId === doc.id}
                    style={{
                      padding: '8px 18px',
                      background: 'white',
                      color: '#4f46e5',
                      border: '1.5px solid #4f46e5',
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      cursor: downloadingDocId === doc.id ? 'wait' : 'pointer',
                      opacity: downloadingDocId === doc.id ? 0.6 : 1,
                      fontFamily: 'inherit',
                    }}
                  >
                    {downloadingDocId === doc.id ? 'Preparing…' : 'Download'}
                  </button>
                )}
              </div>
            );
          })}
          {invoiceNotice && (
            <div role="status" style={{
              marginTop: 10, padding: '8px 12px', background: '#fef3c7',
              borderRadius: 8, color: '#854d0e', fontSize: '0.8125rem',
            }}>
              {invoiceNotice}
            </div>
          )}
        </Card>
      )}

      {/* Pickup address card (only when Garagat drives the pickup leg) */}
      {garagatPickup && order.pickupAddress && (
        <Card title="Pickup Address">
          {order.pickupAddress.fullAddress && (
            <InfoRow label="Address" value={order.pickupAddress.fullAddress} />
          )}
          {order.pickupAddress.latitude != null && order.pickupAddress.longitude != null && (
            <InfoRow
              label="Map"
              value={
                <a
                  href={`https://maps.google.com/?q=${order.pickupAddress.latitude},${order.pickupAddress.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#4f46e5', textDecoration: 'underline', fontSize: '0.875rem' }}
                >
                  Open in Google Maps
                </a>
              }
            />
          )}
        </Card>
      )}

      {/* Admin resolution banner (read-only) */}
      {order.resolutionMessage && (
        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 12,
          padding: '16px 20px',
          marginBottom: 16,
        }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
            Admin resolution:
          </div>
          <div style={{ fontSize: '0.9375rem', color: '#1e3a8a', whiteSpace: 'pre-wrap' }}>
            {order.resolutionMessage}
          </div>
        </div>
      )}

      {/* Assigned driver card. Spec 072 v2 (AC-15): each leg also says how the car travels and when.
          No insurance and no provider cost here — the partner API carries neither. */}
      <Card title="Assigned Driver">
        {order.pickupDeliveries && order.pickupDeliveries.length > 0 ? (
          order.pickupDeliveries.map((pd) => (
            <div key={pd.id} data-testid="assigned-driver-leg" style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>
                {legLabel(pd.type)}
                {transportLabel(order.transportType) && (
                  <span
                    data-testid="leg-transport-chip"
                    style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                      background: order.transportType === 'ZOFEUR' ? '#ccfbf1' : '#f1f5f9',
                      color: order.transportType === 'ZOFEUR' ? '#115e59' : '#334155',
                      fontSize: '0.75rem', fontWeight: 700,
                    }}
                  >
                    {transportLabel(order.transportType)}
                  </span>
                )}
              </div>
              <InfoRow label="Driver" value={pd.driverName || '—'} />
              <InfoRow
                label="Phone"
                value={
                  pd.driverPhone ? (
                    <a
                      href={`tel:${formatPhoneIntl(pd.driverPhone)}`}
                      style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: 500 }}
                    >
                      {formatPhoneIntl(pd.driverPhone)}
                    </a>
                  ) : (
                    '—'
                  )
                }
              />
              <InfoRow label="Scheduled" value={<span data-testid="leg-scheduled">{legScheduledText(pd, order)}</span>} />
              <InfoRow
                label="Status"
                value={
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                    background: statusColor(pd.status).bg, color: statusColor(pd.status).fg,
                    fontSize: '0.75rem', fontWeight: 700,
                  }}>
                    {pd.status}
                  </span>
                }
              />
            </div>
          ))
        ) : (
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>No driver assigned yet.</div>
        )}
      </Card>

      {/* Order history card (timeline) */}
      <Card title="Order History">
        {order.events && order.events.length > 0 ? (
          <div>
            {[...order.events]
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
              .map((ev, idx, arr) => (
                <div key={ev.id} style={{ display: 'flex', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#4f46e5', marginTop: 4 }} />
                    {idx < arr.length - 1 && (
                      <span style={{ flex: 1, width: 2, background: '#e5e7eb', margin: '2px 0' }} />
                    )}
                  </div>
                  <div style={{ paddingBottom: idx < arr.length - 1 ? 16 : 0, flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                      {eventLabel(ev.eventType)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                      {[actorText(ev, auth.user?.id), new Date(ev.createdAt).toLocaleString()]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>No history yet.</div>
        )}
      </Card>

      {/* Action panel */}
      {showActions && (
        <div style={{
          background: 'white',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          padding: '20px 24px',
          marginBottom: 16,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>
            Actions
          </h3>

          {actionError && (
            <div style={{ padding: '10px 14px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: '0.875rem', marginBottom: 12 }}>
              {actionError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {showVehicleReceived && (
              <ActionButton
                label="Vehicle received"
                busyLabel="Recording…"
                doneLabel="Vehicle received ✓"
                busy={pendingAction === 'vehicle-received'}
                done={Boolean(completedActions['vehicle-received'])}
                disabled={actionLoading}
                background="#4f46e5"
                onClick={handleVehicleReceived}
              />
            )}
            {showMarkComplete && (
              <ActionButton
                label="Mark finished"
                busyLabel="Recording…"
                doneLabel="Marked finished ✓"
                busy={pendingAction === 'service-complete'}
                done={false}
                disabled={actionLoading}
                background="#16a34a"
                onClick={handleMarkServiceComplete}
              />
            )}
            {showCollected && (
              <ActionButton
                label="Collected by customer"
                busyLabel="Recording…"
                doneLabel="Collected by customer ✓"
                busy={pendingAction === 'collected'}
                done={Boolean(completedActions['collected'])}
                disabled={actionLoading}
                background="#16a34a"
                onClick={handleCollectedByCustomer}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
